import { ConversionTrackingContextProvider } from "@circle-react/contexts/ConversionTrackingContext";
import { LayoutPartialsContextProvider } from "@circle-react/contexts/layoutPartialsContext";
import { PunditUserContextProvider } from "@circle-react/contexts/punditUserContext";
import { SpaceGroupsContextProvider } from "@circle-react/contexts/spaceGroupsContext";
import { SpacesContextProvider } from "@circle-react/contexts/spacesContext";
import type { ThemeObject } from "@circle-react/types/Theme";
import { CompassProvider } from "@circleco/compass/context/CompassProvider";
import type { CompassLocale } from "@circleco/compass/locale/types";
import { useEffect, useMemo, useState } from "react";
import { CookiesProvider } from "react-cookie";

import { ViewOnlyModeToastBridge } from "@/react/components/SettingsApp/CompassCompat/ViewOnlyModeToastBridge";
import { ToastProvider } from "@/react/components/shared/uikit/ToastV2";
import { loadCompassLocale } from "@/react/config/Locales/Compass";

import type { AppContextProviderProps } from "../AppContext";
import { AppContextProvider } from "../AppContext";
import { I18nProvider } from "../I18nProvider";
import { QueryClientProvider } from "../QueryClient";
import { CommunityThemeProvider } from "./CommunityThemeProvider";

export interface MainAppProviders {
  backendProps?: AppContextProviderProps["backendProps"];
  children: React.ReactNode;
}

// "backendProps" comes from app/views/shared/_react_app.html.erb
export const MainAppProviders = ({
  children,
  backendProps = {},
}: MainAppProviders) => {
  const [compassLocale, setCompassLocale] = useState<CompassLocale>();

  useEffect(() => {
    void loadCompassLocale().then(setCompassLocale);
  }, []);

  const {
    community_theme_settings = {},
    brand_color = {
      light: "",
      dark: "",
    },
    brand_text_color = {
      light: "",
      dark: "",
    },
  } = backendProps;

  const theme = useMemo<ThemeObject>(
    () => ({
      ...community_theme_settings,
      brand_color,
      brand_text_color,
    }),
    [brand_color, brand_text_color, community_theme_settings],
  );

  return (
    <CompassProvider locale={compassLocale}>
      <I18nProvider>
        <QueryClientProvider>
          <PunditUserContextProvider>
            <CommunityThemeProvider fallbackTheme={theme}>
              <AppContextProvider backendProps={backendProps}>
                <LayoutPartialsContextProvider>
                  <SpaceGroupsContextProvider>
                    <SpacesContextProvider includeSidebar>
                      <ToastProvider>
                        <ViewOnlyModeToastBridge />
                        <CookiesProvider>
                          <ConversionTrackingContextProvider>
                            {children}
                          </ConversionTrackingContextProvider>
                        </CookiesProvider>
                      </ToastProvider>
                    </SpacesContextProvider>
                  </SpaceGroupsContextProvider>
                </LayoutPartialsContextProvider>
              </AppContextProvider>
            </CommunityThemeProvider>
          </PunditUserContextProvider>
        </QueryClientProvider>
      </I18nProvider>
    </CompassProvider>
  );
};
